/**
 * One timezone for the whole app: the exchange's.
 *
 * Every date and clock time this product shows describes market activity, and
 * market activity happens on New York's clock. Rendering it in the viewer's
 * local zone does not help them — it silently relabels a session. A 3:58pm
 * sweep is a 3:58pm sweep whether you are reading it in London or Denver, and
 * showing it as 20:58 or 13:58 breaks the only reference that matters, which
 * is where it sat relative to the close.
 *
 * The date case is worse than the time case: a date-only value formatted in
 * local time renders as the PREVIOUS day anywhere west of Greenwich, so an
 * expiry or an earnings date is simply wrong rather than merely relabelled.
 *
 * Pass `TZ` to every `toLocale*` call that renders market data.
 */
export const TZ = 'America/New_York';

/**
 * Today's date in market terms, as `YYYY-MM-DD`.
 *
 * NOT `new Date().toISOString().slice(0, 10)`. That converts the viewer's
 * local instant to UTC, so anyone in the Americas rolls over to tomorrow during
 * the evening — a New York user's "today" changes at 8pm, hours after the close
 * but while it is still the same trading day. Someone in Asia is a full day out
 * for most of their working hours.
 */
export function todayEt(): string {
  // en-CA formats as YYYY-MM-DD, which is the shape we want anyway.
  return new Date().toLocaleDateString('en-CA', { timeZone: TZ });
}

/** The current market-calendar year. */
export function currentYearEt(): number {
  return Number(todayEt().slice(0, 4));
}

/**
 * The date part of a timestamp, in market terms, as `YYYY-MM-DD`.
 *
 * Use this rather than `toDateString()` comparisons when deciding whether
 * something happened today — `toDateString()` is local.
 */
export function etDate(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

/**
 * Parses a value that may be a date-only string into a Date safe to format in
 * Eastern.
 *
 * Date-only strings are anchored to **12:00 UTC**, not local noon. Local noon
 * formatted in Eastern goes backwards a day for anyone far enough east: noon in
 * Tokyo is 23:00 the previous evening in New York. Midday UTC is 07:00–08:00 ET
 * on the same calendar day from anywhere.
 */
export function parseMarketDate(s: string): Date {
  const iso = s.length === 10 ? `${s}T12:00:00Z` : s.replace(' ', 'T');
  return new Date(iso);
}
